import { useEffect, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/api/client";
import { useAuth } from "@/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { ChevronLeft, Download, Share2, Loader2 } from "lucide-react";
import { toPng } from "html-to-image";
import QRCode from "qrcode";
import { toast } from "sonner";
import { buildInviteUrl, appUrl } from "@/lib/share";
import { teamDe } from "@/lib/teamNames";

type Row = {
  game: any;
  tip: { home_tip: number; away_tip: number; points_earned: number } | null;
};

export default function Spieltag() {
  const { date } = useParams<{ date: string }>();
  const { user, profile } = useAuth();
  const [rows, setRows] = useState<Row[]>([]);
  const [qr, setQr] = useState<string>("");
  const [busy, setBusy] = useState(false);
  const cardRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    document.title = `Spieltag ${date} – Englputzeder Tippspiel`;
    QRCode.toDataURL(buildInviteUrl(), { width: 200, margin: 1 }).then(setQr).catch(() => {});
  }, [date]);

  useEffect(() => {
    if (!user || !date) return;
    (async () => {
      // The API doesn't expose a date range filter — we fetch all and slice
      // client-side. This list is bounded (~104 games) so it's fine.
      const allGames = (await api.games.list()) as any[];
      const dayGames = allGames
        .filter((g) => (g.kickoffTime as string).slice(0, 10) === date)
        .sort((a, b) => (a.kickoffTime as string).localeCompare(b.kickoffTime as string))
        .map((g) => ({
          ...g,
          home_flag: g.homeFlag, away_flag: g.awayFlag,
          home_team: g.homeTeam, away_team: g.awayTeam,
          home_score: g.homeScore, away_score: g.awayScore,
          is_finished: g.isFinished,
        }));
      const allTips = (await api.tips.listMine()) as any[];
      const dayIds = new Set(dayGames.map((g: any) => g.id));
      const tipsByGame: Record<string, any> = {};
      allTips.forEach((t: any) => {
        if (dayIds.has(t.gameId)) {
          tipsByGame[t.gameId] = { home_tip: t.homeTip, away_tip: t.awayTip, points_earned: t.pointsEarned };
        }
      });
      setRows(dayGames.map((g: any) => ({ game: g, tip: tipsByGame[g.id] ?? null })));
    })();
  }, [user, date]);

  const totalPoints = rows.reduce((s, r) => s + (r.tip?.points_earned ?? 0), 0);
  const tipped = rows.filter(r => r.tip).length;

  const downloadCard = async () => {
    if (!cardRef.current) return;
    setBusy(true);
    try {
      const dataUrl = await toPng(cardRef.current, { pixelRatio: 2, cacheBust: true });
      const link = document.createElement("a");
      link.download = `spieltag-${date}.png`;
      link.href = dataUrl;
      link.click();
      toast.success("Karte gespeichert!");
    } catch (e: any) {
      toast.error("Fehler beim Erstellen");
    } finally {
      setBusy(false);
    }
  };

  const shareCard = async () => {
    if (!cardRef.current) return;
    setBusy(true);
    try {
      const dataUrl = await toPng(cardRef.current, { pixelRatio: 2, cacheBust: true });
      const blob = await (await fetch(dataUrl)).blob();
      const file = new File([blob], `spieltag-${date}.png`, { type: "image/png" });
      const navAny = navigator as any;
      if (navAny.canShare?.({ files: [file] })) {
        await navAny.share({ files: [file], title: "Mein Spieltag", text: "Mein WM-Spieltag bei Englputzeder ⚽" });
      } else {
        const wa = `https://wa.me/?text=${encodeURIComponent("Mein WM-Spieltag bei Englputzeder ⚽\n" + buildInviteUrl())}`;
        window.open(wa, "_blank");
      }
    } catch (e) { /* user cancel */ }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-4">
      <Link to="/" className="flex items-center gap-1 text-sm text-muted-foreground tap">
        <ChevronLeft className="h-4 w-4" /> Zurück
      </Link>

      <h1 className="font-display font-extrabold text-2xl">Mein Spieltag</h1>

      {/* Story Card 1080x1920 — scaled in preview */}
      <div className="overflow-hidden rounded-lg shadow-pop bg-muted">
        <div
          ref={cardRef}
          style={{ width: 1080, height: 1920, transform: "scale(0.32)", transformOrigin: "top left" }}
          className="bg-hero text-primary-foreground flex flex-col p-20 relative"
        >
          <div className="text-xl font-bold uppercase tracking-[0.3em] opacity-80">Englputzeder · WM 2026</div>
          <div className="font-display font-extrabold text-7xl mt-6 leading-tight">
            Mein<br/>Spieltag
          </div>
          <div className="text-3xl font-bold mt-4 opacity-90">
            {new Date(date + "T12:00:00").toLocaleDateString("de-AT", { weekday: "long", day: "2-digit", month: "long" })}
          </div>

          <div className="mt-12 bg-primary-foreground/15 backdrop-blur rounded-3xl p-10">
            <div className="text-2xl font-bold opacity-80">{profile?.emoji} {profile?.username}</div>
            <div className="grid grid-cols-2 gap-6 mt-6">
              <div className="bg-primary-foreground/10 rounded-2xl p-6 text-center">
                <div className="text-sm font-bold uppercase tracking-wider opacity-70">Punkte heute</div>
                <div className="font-display font-extrabold text-7xl mt-2 tabular-nums">{totalPoints}</div>
              </div>
              <div className="bg-primary-foreground/10 rounded-2xl p-6 text-center">
                <div className="text-sm font-bold uppercase tracking-wider opacity-70">Tipps</div>
                <div className="font-display font-extrabold text-7xl mt-2 tabular-nums">{tipped}/{rows.length}</div>
              </div>
            </div>
          </div>

          <div className="flex-1 mt-10 space-y-4 overflow-hidden">
            {rows.slice(0, 6).map(r => (
              <div key={r.game.id} className="bg-primary-foreground/10 rounded-2xl p-6 flex items-center gap-4">
                <div className="text-4xl">{r.game.home_flag}</div>
                <div className="flex-1 min-w-0">
                  <div className="text-2xl font-bold truncate">{teamDe(r.game.home_team)} – {teamDe(r.game.away_team)}</div>
                  <div className="text-lg opacity-70">
                    {r.tip ? `Tipp ${r.tip.home_tip}:${r.tip.away_tip}` : "kein Tipp"}
                    {r.game.is_finished && r.game.home_score != null && (
                      <> · Ergebnis {r.game.home_score}:{r.game.away_score}</>
                    )}
                  </div>
                </div>
                <div className="text-4xl">{r.game.away_flag}</div>
                {r.tip && r.tip.points_earned > 0 && (
                  <div className="bg-gold text-foreground rounded-full px-5 py-2 font-extrabold text-3xl tabular-nums">
                    +{r.tip.points_earned}
                  </div>
                )}
              </div>
            ))}
          </div>

          <div className="mt-10 flex items-end justify-between">
            <div>
              <div className="text-2xl font-bold">Tippst du auch mit?</div>
              {/* Dynamic host so the share card always shows the domain it
                  was generated on — matches the QR code, which is built
                  from the same appUrl(). */}
              <div className="text-3xl font-extrabold mt-1">{appUrl().replace(/^https?:\/\//, "")}</div>
            </div>
            {qr && <img src={qr} alt="QR" className="w-44 h-44 bg-primary-foreground p-3 rounded-2xl" />}
          </div>
        </div>
        <div style={{ height: 1920 * 0.32, marginTop: -1920 }} aria-hidden />
      </div>

      <div className="grid grid-cols-2 gap-2">
        <Button onClick={downloadCard} disabled={busy} className="h-12">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4 mr-2" />}
          PNG laden
        </Button>
        <Button onClick={shareCard} disabled={busy} variant="outline" className="h-12">
          <Share2 className="h-4 w-4 mr-2" />
          Teilen
        </Button>
      </div>

      <div className="bg-card rounded-lg shadow-card p-4 text-sm text-muted-foreground">
        💡 Tipp: Teile deine Karte als WhatsApp-Status oder Insta-Story — je mehr Freunde mittippen, desto höher die Chance aufs Sponsoring eurer Gruppe.
      </div>
    </div>
  );
}
